import { useState, useCallback, useMemo, useEffect } from 'react'
import { getTextTriviaAnswerResult } from 'services/texttrivia'

const checkAnswer = async (triviaId, questionId, answerId) => {
  try {
    const response = await getTextTriviaAnswerResult(
      triviaId,
      questionId,
      answerId,
    )
    return response.detail
  } catch (e) {
    console.warn('Trivia CheckAnswer', e)
  }
}

const ANALYTICS_EVENTS = {
  TRIVIA_ANSWER_SELECTED: 'step_interaction_trivia_answer_selected',
  TRIVIA_RESULT_SHOWN: 'step_interaction_trivia_result',
}

/**
 * Custom hook for managing a text trivia game flow
 * @param {Object} params
 * @param {Object[]} params.questions - Array of question objects
 * @param {Object[]} params.results - Array of result objects
 * @param {number} params.maxTime - Maximum time for the trivia
 * @param {string} params.triviaId - The ID of the trivia
 * @param {Function} params.reInitWhenImagesReady - Re-measures the carousel once the current question's images have loaded
 * @param {Function} params.onEngage - The function to call when the trivia is engaged
 * @param {Function} params.onAwardPoints - The function to call when the trivia is awarded points
 * @param {Object} params.analytics - The analytics object
 * @param {Object} params.step - The step object
 * @param {Object} params.trivia - The trivia object
 * @param {number} params.possibleCoinsAwarded - The number of coins awarded for the trivia
 * @param {boolean} params.useTimer - Whether the question timer is enabled (Beaker toggle; defaults to on)
 * @returns {Object} Hook state and handlers
 */
export const useTextTrivia = ({
  questions: questionsProp,
  results: resultsProp,
  maxTime = 90,
  triviaId,
  reInitWhenImagesReady,
  onEngage,
  onAwardPoints,
  analytics,
  step,
  trivia,
  possibleCoinsAwarded,
  useTimer = true,
}) => {
  const [score, setScore] = useState(0)
  const [questions, setQuestions] = useState(questionsProp)
  const [resultScreen, setResultScreen] = useState(null)
  const [complete, setComplete] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showRightAnswer, setShowRightAnswer] = useState(false)
  const [currentTime, setCurrentTime] = useState(maxTime)
  const [selectedAnswers, setSelectedAnswers] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const [showResultScreen, setShowResultScreen] = useState(false)
  const [hasAnswered, setHasAnswered] = useState(false)
  const [wasCorrect, setWasCorrect] = useState(false)

  useEffect(() => {
    if (!useTimer) return
    if (currentTime > 0 && hasStarted) {
      const interval = setInterval(() => {
        setCurrentTime(prev => prev - 1)
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [currentTime, hasStarted, useTimer])

  const currentQuestion = useMemo(() => {
    return questions[currentQuestionIndex] || null
  }, [questions, currentQuestionIndex])

  const isLastQuestion = useMemo(() => {
    return currentQuestionIndex === questions.length - 1
  }, [currentQuestionIndex, questions])

  const handleSelectAnswer = useCallback(
    answerId => {
      if (selectedAnswers[currentQuestion.id] === answerId) return

      analytics.stepEvent(ANALYTICS_EVENTS.TRIVIA_ANSWER_SELECTED, step, {
        context_id: currentQuestion.id,
        context: currentQuestion.question,
        context_total: score,
        context_position: currentQuestionIndex,
        result_id: answerId,
        result: currentQuestion.answers.find(answer => answer.id === answerId)
          ?.text,
      })

      setHasAnswered(true)
      setSelectedAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: answerId,
      }))
    },
    [
      currentQuestion,
      selectedAnswers,
      analytics,
      step,
      score,
      currentQuestionIndex,
    ],
  )

  const handleNextQuestion = useCallback(() => {
    if (isLastQuestion) {
      setCurrentTime(0)
      setComplete(true)
      setShowResultScreen(true)

      analytics.stepEvent(ANALYTICS_EVENTS.TRIVIA_RESULT_SHOWN, step, {
        context_id: trivia.id,
        context: trivia.headline,
        result_id: resultScreen?.id,
        result: resultScreen?.title,
        context_position: currentQuestionIndex + 1,
      })

      onEngage()
      onAwardPoints()
    } else {
      setCurrentTime(maxTime)
      setComplete(false)
      setCurrentQuestionIndex(prev => prev + 1)
      setHasAnswered(false)
      setWasCorrect(false)
    }
    setShowRightAnswer(false)
  }, [
    analytics,
    currentQuestionIndex,
    isLastQuestion,
    maxTime,
    onEngage,
    resultScreen?.id,
    resultScreen?.title,
    step,
    trivia.headline,
    trivia.id,
    onAwardPoints,
  ])

  const reorderGridInImageCase = (answers, correctAnswerId) => {
    if (answers.some(answer => answer.image)) {
      const correctAnswer = answers.find(
        answer => answer.id === correctAnswerId,
      )
      const otherAnswers = answers.filter(
        answer => answer.id !== correctAnswerId,
      )
      return [...otherAnswers, correctAnswer]
    }
    return answers
  }

  const handleCheckAnswer = useCallback(
    async (selectedAnswers = {}) => {
      let currentScore = score
      if (!questions || isLoading) return

      setIsLoading(true)

      try {
        const answerId = selectedAnswers[currentQuestion.id]
        const { correct, correctAnswerId } = await checkAnswer(
          triviaId,
          currentQuestion.id,
          answerId,
        )
        if (correct && hasAnswered) {
          currentScore = currentScore + 1
          setScore(currentScore)
          setWasCorrect(true)
        }

        const questionsWithResults = questions?.map(question => {
          if (question.id === currentQuestion.id) {
            const answers = reorderGridInImageCase(
              question.answers,
              correctAnswerId,
            )

            return {
              ...question,
              answers,
              correct,
              correctAnswerId,
              wasAnswered: true,
            }
          }
          return question
        })

        setQuestions(questionsWithResults)
        setShowRightAnswer(true)
        setComplete(true)
        setCurrentTime(-1)

        if (isLastQuestion) {
          const result = resultsProp.find(
            result =>
              result.minCorrect <= currentScore &&
              result.maxCorrect >= currentScore,
          )

          setResultScreen(result)
        }
      } finally {
        setIsLoading(false)
        reInitWhenImagesReady?.()
      }
    },
    [
      score,
      questions,
      isLoading,
      currentQuestion.id,
      triviaId,
      trivia,
      hasAnswered,
      isLastQuestion,
      resultsProp,
      analytics,
      step,
      currentQuestionIndex,
      onAwardPoints,
      possibleCoinsAwarded,
      reInitWhenImagesReady,
    ],
  )

  // Remove the separate effects and combine them into one
  useEffect(() => {
    // Timer off: nothing can time out, the question only ends when the user
    // submits an answer.
    if (!useTimer) return

    if (currentTime === 0 && !complete) {
      setComplete(true)
      setShowRightAnswer(true)

      // Only check answer if we haven't already
      if (
        !isLoading &&
        Object.keys(selectedAnswers).length <= currentQuestionIndex
      ) {
        setSelectedAnswers(prev => ({
          ...prev,
          [currentQuestion.id]: {},
        }))
        handleCheckAnswer({
          [currentQuestion.id]: currentQuestion.answers[0].id,
        })
      } else if (Object.keys(selectedAnswers).length > currentQuestionIndex) {
        handleCheckAnswer(selectedAnswers)
      }
    }
  }, [
    useTimer,
    currentTime,
    complete,
    isLoading,
    selectedAnswers,
    currentQuestion,
    handleCheckAnswer,
    currentQuestionIndex,
  ])

  const startTrivia = useCallback(() => {
    setCurrentTime(maxTime)
    setHasStarted(true)
  }, [maxTime])

  // Return values and functions needed by the component
  return {
    currentQuestionIndex,
    selectedAnswers,
    totalQuestions: questions.length,
    handleSelectAnswer,
    handleNextQuestion,
    handleCheckAnswer,
    isLoading,
    currentTime,
    complete,
    showRightAnswer,
    questions,
    hasStarted,
    startTrivia,
    score,
    resultScreen,
    showResultScreen,
    hasAnswered,
    wasCorrect,
  }
}
